import { useState, useRef } from "react";
import { X, Download, Upload, FileSpreadsheet, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import apiClient from "../../lib/apiClient";
import ToastMsg from "../../components/common/ToastMsg";
import {
  ONBOARDED_BULK_TEMPLATE_URL,
  ONBOARDED_BULK_UPLOAD_URL,
} from "../../constants/api";

/**
 * Bulk-upload onboarded clients from an XLSX file.
 * Two steps in one modal: download template, then upload the filled file.
 * Duplicate rows (by case-insensitive org_name within the project) are skipped server-side.
 */
const BulkUploadOnboardedModal = ({ onClose, onSuccess }) => {
  const { create } = useApiMethods();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const data = await apiClient.get(ONBOARDED_BULK_TEMPLATE_URL, {
        responseType: "blob",
        timeout: 60000,
      });
      const blob =
        data instanceof Blob
          ? data
          : new Blob([data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "onboarded_clients_template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error("Template download failed", e);
      ToastMsg("Failed to download template", "error");
    } finally {
      setDownloading(false);
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xlsx|xls)$/i.test(f.name)) {
      ToastMsg("Please choose an .xlsx or .xls file", "error");
      return;
    }
    setFile(f);
    setResult(null);
  };

  const submit = async () => {
    if (!file) {
      ToastMsg("Choose a file first", "error");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await create(ONBOARDED_BULK_UPLOAD_URL, fd, true);
      if (res?.status === "success") {
        setResult(res);
        ToastMsg(res.message || "Bulk upload complete", "success");
        if (typeof onSuccess === "function") onSuccess();
      } else {
        ToastMsg(res?.message || "Upload failed", "error");
      }
    } catch (e) {
      console.error("Bulk upload failed", e);
      ToastMsg("Upload failed", "error");
    } finally {
      setUploading(false);
    }
  };

  const close = () => {
    if (uploading) return;
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-lg bg-white dark:bg-gray-900 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 px-5 py-3">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              Bulk Upload Onboarded Clients
            </h2>
          </div>
          <button
            onClick={close}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            disabled={uploading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Step 1 — download template */}
          <div className="rounded-md border border-gray-200 dark:border-gray-800 p-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  Step 1 — Download the template
                </div>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  One row per client. Includes client fields, optional AMC and one primary contact.
                  Required columns are marked with <span className="font-semibold">*</span>.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadTemplate}
                disabled={downloading}
                className="flex-shrink-0 flex items-center gap-1.5"
              >
                <Download className="h-4 w-4" />
                {downloading ? "Generating…" : "Template"}
              </Button>
            </div>
          </div>

          {/* Step 2 — upload */}
          <div className="rounded-md border border-gray-200 dark:border-gray-800 p-3">
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              Step 2 — Upload the filled file
            </div>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Rows whose org_name already exists in this project will be skipped automatically.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={onPick}
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-1.5"
              >
                <Upload className="h-4 w-4" />
                Choose file
              </Button>
              <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
                {file ? file.name : "No file chosen"}
              </span>
            </div>
          </div>

          {/* Result */}
          {result && (
            <div className="rounded-md border border-gray-200 dark:border-gray-800 p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                Done
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="rounded bg-green-50 dark:bg-green-900/20 p-2">
                  <div className="text-green-700 dark:text-green-300 font-semibold">{result.created || 0}</div>
                  <div className="text-gray-500">Created</div>
                </div>
                <div className="rounded bg-amber-50 dark:bg-amber-900/20 p-2">
                  <div className="text-amber-700 dark:text-amber-300 font-semibold">{result.skipped_duplicates || 0}</div>
                  <div className="text-gray-500">Skipped (duplicate)</div>
                </div>
                <div className="rounded bg-red-50 dark:bg-red-900/20 p-2">
                  <div className="text-red-700 dark:text-red-300 font-semibold">{result.errors?.length || 0}</div>
                  <div className="text-gray-500">Errors</div>
                </div>
              </div>
              {!!result.errors?.length && (
                <div className="mt-1 max-h-40 overflow-auto rounded border border-red-200 dark:border-red-900/40 p-2 text-xs">
                  <div className="flex items-center gap-1 font-medium text-red-700 dark:text-red-300 mb-1">
                    <AlertTriangle className="h-3.5 w-3.5" /> Error details
                  </div>
                  <ul className="space-y-1">
                    {result.errors.map((er, i) => (
                      <li key={i} className="text-red-700 dark:text-red-300">
                        Row {er.row}{er.org_name ? ` (${er.org_name})` : ""}: {er.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-gray-200 dark:border-gray-800 px-5 py-3">
          <Button variant="outline" size="sm" onClick={close} disabled={uploading}>
            {result ? "Close" : "Cancel"}
          </Button>
          <Button
            size="sm"
            onClick={submit}
            disabled={!file || uploading}
            className="flex items-center gap-1.5"
          >
            <Upload className="h-4 w-4" />
            {uploading ? "Uploading…" : "Upload"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadOnboardedModal;
